import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CAROUSEL_ITEM_GAP, CAROUSEL_ITEM_WIDTH } from './shared';
export type DynamicListCarouselItem = {
	id: number;
	position: number;
};

type DynamicListCarouselContextValue = {
	positions: DynamicListCarouselItem[];
	stepRight: () => void;
	stepLeft: () => void;
	carouselWidth: number;
	carouselState: DynamicListCarouselState;
};

const DynamicListCarouselContext = createContext<DynamicListCarouselContextValue | undefined>(undefined);

type DynamicListCarouselContextProviderProps = {
	items: DynamicListCarouselItem[];
	carouselWidth: number;
	children: React.ReactNode;
};

type DynamicListCarouselState = {
	showLeft: boolean;
	showRight: boolean;
	partiallyVisibleItems: number[];
	fullyObscuredItems: number[];
};

function useCarouselState(): {
	carouselState: DynamicListCarouselState;
	updateCarouselState: (carouselWidth: number, positions: DynamicListCarouselItem[]) => void;
} {
	const [carouselState, setCarouselState] = useState<DynamicListCarouselState>();

	function updateCarouselState(carouselWidth: number, positions: DynamicListCarouselItem[]) {
		const totalItemWidth = CAROUSEL_ITEM_WIDTH + CAROUSEL_ITEM_GAP;

		const result = {
			showLeft: false,
			showRight: false,
			partiallyVisibleItems: [],
			fullyObscuredItems: []
		};

		for (const item of positions) {
			const itemLeft = item.position * totalItemWidth;
			const itemRight = itemLeft + CAROUSEL_ITEM_WIDTH;

			const fullyVisible = itemLeft >= 0 && itemRight <= carouselWidth;
			const partiallyVisible = (itemLeft < 0 && itemRight > 0) || (itemLeft < carouselWidth && itemRight > carouselWidth);

			if (partiallyVisible) {
				result.partiallyVisibleItems.push(item.id);
			} else if (!fullyVisible) {
				result.fullyObscuredItems.push(item.id);
			}

			if (itemRight > carouselWidth) {
				result.showRight = true;
			}
			if (itemLeft < 0) {
				result.showLeft = true;
			}
		}

		setCarouselState(result);
	}

	return { carouselState, updateCarouselState };
}

export const DynamicListCarouselContextProvider: React.FC<DynamicListCarouselContextProviderProps> = props => {
	const { items, carouselWidth } = props;

	const [positions, setPositions] = useState<{ id: number; position: number }[]>(items);
	const { carouselState, updateCarouselState } = useCarouselState();

	useEffect(() => {
		updateCarouselState(carouselWidth, positions);
	}, [carouselWidth]);

	const stepRight = useCallback(() => {
		const newPositions = carouselState.showRight ? positions.map(p => ({ ...p, position: p.position - 1 })) : positions;
		setPositions(newPositions);
		updateCarouselState(carouselWidth, newPositions);
	}, [positions, carouselWidth, carouselState?.showRight]);

	const stepLeft = useCallback(() => {
		const newPositions = carouselState.showLeft ? positions.map(p => ({ ...p, position: p.position + 1 })) : positions;
		setPositions(newPositions);
		updateCarouselState(carouselWidth, newPositions);
	}, [positions, carouselWidth, carouselState?.showLeft]);

	return (
		<DynamicListCarouselContext.Provider
			value={{
				carouselWidth,
				positions,
				stepRight,
				stepLeft,
				carouselState
			}}
		>
			{carouselState ? props.children : null}
		</DynamicListCarouselContext.Provider>
	);
};

export const useDynamicListCarouselContext = () => useContext(DynamicListCarouselContext);
